import React from 'react';
import { styled } from '../../../../../shared/Theme/theme';
import { bpAccountingKpis } from '../../../../../../models/entities/businessPartner/accounting';
import {
    FolderOpenOutlined,
    WarningOutlined,
    CalendarOutlined,
    DollarOutlined,
    InfoCircleOutlined,
    InfoCircleFilled
} from '@ant-design/icons';
import { formatMoney } from './format';

type Props = {
    kpis: bpAccountingKpis;
};

const KpiCards = ({ kpis }: Props) => {
    const fmt = (v: number) => formatMoney(v, kpis.currency);

    return (
        <Row>
            <PrimaryCard>
                <CardHeader>
                    <IconBox $bg="#fde7e9" $color="#c0392b">
                        <FolderOpenOutlined />
                    </IconBox>
                    <CardTitle>Total Open Balance</CardTitle>
                    <InfoBadge>
                        <InfoCircleFilled style={{ color: '#e74c3c', fontSize: 12 }} />
                    </InfoBadge>
                </CardHeader>
                <PrimaryValue>{fmt(kpis.totalOpenBalance)}</PrimaryValue>
            </PrimaryCard>

            <Card>
                <CardHeader>
                    <IconBox $bg="#fff3d6" $color="#c0392b">
                        <WarningOutlined />
                    </IconBox>
                    <CardTitle>Overdue {kpis.paymentTermsLabel ? `(${kpis.paymentTermsLabel})` : ''}</CardTitle>
                </CardHeader>
                <DangerValue>{fmt(kpis.overdueAmount)}</DangerValue>
                <SubText>{kpis.overdueShipmentCount} shipments</SubText>
            </Card>

            <Card>
                <CardHeader>
                    <IconBox $bg="#fff3d6" $color="#a56b1c">
                        <CalendarOutlined />
                    </IconBox>
                    <CardTitle>Due Soon (1–30 days)</CardTitle>
                </CardHeader>
                <WarnValue>{fmt(kpis.dueSoonAmount)}</WarnValue>
                <SubText>{kpis.dueSoonShipmentCount} shipments</SubText>
            </Card>

            <Card>
                <CardHeader>
                    <IconBox $bg="#e7f6ec" $color="#1f7a3a">
                        <DollarOutlined />
                    </IconBox>
                    <CardTitle>Paid (Last 60 Days)</CardTitle>
                </CardHeader>
                <PaidValue>{fmt(kpis.paidLast60)}</PaidValue>
            </Card>

            <Card>
                <CardHeader>
                    <IconBox $bg="#e8f4fc" $color="#1f7ab8">
                        <InfoCircleOutlined />
                    </IconBox>
                    <CardTitle>Available Credit</CardTitle>
                </CardHeader>
                <InfoValue>{fmt(kpis.availableCredit)}</InfoValue>
                <SubText>Credit Line: {fmt(kpis.creditLine)}</SubText>
            </Card>
        </Row>
    );
};

const Row = styled.div`
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 14px;
    margin: 14px 0 18px;
`;

const cardBase = `
    border-radius: 10px;
    padding: 14px 16px;
    min-height: 104px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: #fff;
`;

const Card = styled.div`
    ${cardBase}
    border: 1px solid #ececec;
    box-shadow: 0 1px 2px rgba(20, 30, 60, 0.04);
`;

const PrimaryCard = styled.div`
    ${cardBase}
    background: #fff5f5;
    border: 1px solid #f8c4c4;
    box-shadow: 0 1px 2px rgba(192, 57, 43, 0.06);
    position: relative;
    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 14px;
        bottom: 14px;
        width: 3px;
        border-radius: 2px;
        background: #e74c3c;
    }
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
`;

const IconBox = styled.span<{ $bg: string; $color: string }>`
    width: 26px;
    height: 26px;
    background: ${(p) => p.$bg};
    color: ${(p) => p.$color};
    border-radius: 6px;
    font-size: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const CardTitle = styled.span`
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
    flex: 1;
`;

const InfoBadge = styled.span`
    display: inline-flex;
    align-items: center;
`;

const Value = styled.div`
    font-size: 22px;
    font-weight: 700;
    color: #1f2937;
    line-height: 28px;
    letter-spacing: -0.2px;
`;

const PrimaryValue = styled(Value)`
    color: #c0392b;
`;

const DangerValue = styled(Value)`
    color: #c0392b;
`;

const WarnValue = styled(Value)`
    color: #a56b1c;
`;

const PaidValue = styled(Value)`
    color: #1f7a3a;
`;

const InfoValue = styled(Value)`
    color: #1f7ab8;
`;

const SubText = styled.div`
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
`;

export default KpiCards;
